import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, Building2, Wrench, Heart, Activity, Bed, Armchair, Radio, TestTube, Stethoscope, Baby, Zap, HeartPulse, Eye, Bone, Dumbbell, HandHeart, Sun, Droplets, Truck, Shield, FlaskConical, Wind, Pill, Store, Users, Sparkles } from 'lucide-react';

const Products = () => {
  const infrastructureProducts = [
    {
      category: "Modular Operation Theatres & Critical Care",
      icon: <Heart size={32} style={{ color: 'var(--brand-orange)' }} />,
      items: [
        "Turnkey Modular Operation Theatres",
        "Turnkey Modular MICU (Medical ICU)",
        "Turnkey Modular SICU (Surgical ICU)",
        "Turnkey Modular Cardiac ICU",
        "Turnkey Modular Neuro ICU",
        "Turnkey Modular PICU (Pediatric ICU)",
        "Turnkey Modular NICU (Neonatal ICU)",
        "Turnkey Modular Trauma Setup",
        "Turnkey Modular Burns Setup",
        "Turnkey Modular Transplant ICU",
        "Turnkey Modular TB ICU",
        "Turnkey Modular HDU (High Dependency Unit)",
        "Turnkey Modular CVTS OT (Cardio-Vascular Thoracic Surgery)"
      ]
    },
    {
      category: "Diagnostic & Imaging Facilities",
      icon: <Activity size={32} style={{ color: 'var(--brand-orange)' }} />,
      items: [
        "Turnkey Modular Radiology Centre",
        "Turnkey Modular Cath Lab"
      ]
    },
    {
      category: "Support Services Infrastructure",
      icon: <Building2 size={32} style={{ color: 'var(--brand-orange)' }} />,
      items: [
        "Turnkey MGPS System (Medical Gas Pipeline System)",
        "Turnkey CSSD and Laundry",
        "Turnkey Pneumatic Tube Systems",
        "Turnkey Modular OPD Setup",
        "Turnkey Modular Mortuary Setup"
      ]
    },
    {
      category: "Educational & Administrative Facilities",
      icon: <Wrench size={32} style={{ color: 'var(--brand-orange)' }} />,
      items: [
        "Turnkey Auditorium Setup",
        "Interactive Conference Setup for Teaching Institutes"
      ]
    }
  ];

  const medicalFurnitureProducts = [
    {
      category: "Patient Care Furniture",
      icon: <Bed size={32} style={{ color: 'var(--brand-teal)' }} />,
      items: [
        "ICU Solutions",
        "Ward Care Solutions",
        "Patient Transfer Solutions"
      ]
    },
    {
      category: "Clinical & Treatment Furniture",
      icon: <Armchair size={32} style={{ color: 'var(--brand-teal)' }} />,
      items: [
        "Exam Room Solutions",
        "Maternal and Gynaecology Solutions",
        "Multi Treatment Chair",
        "OT Solutions"
      ]
    },
    {
      category: "Support & Mobility",
      icon: <Activity size={32} style={{ color: 'var(--brand-teal)' }} />,
      items: [
        "Mobility Solutions",
        "Operation & Maintenance (O&M)"
      ]
    }
  ];

  const radiologyProducts = [
    {
      category: "Modular Radiology Systems",
      icon: <Radio size={32} style={{ color: 'var(--brand-red)', filter: 'brightness(1.2)' }} />,
      items: [
        "Modular DR System (Digital Radiography)",
        "CR System (Computed Radiography)",
        "Modular Sonography System",
        "Modular Mammography System with Tomography and Biopsy",
        "Advanced Modular Cath Lab with CVTS Modular Operation Theatre",
        "Modular Pre & Post Operative ICU",
        "Modular MRI with RF Shielding / Magnetic Shielding",
        "Modular CT Scan System"
      ]
    },
    {
      category: "Support Services & Compliance",
      icon: <Wrench size={32} style={{ color: 'var(--brand-red)', filter: 'brightness(1.2)' }} />,
      items: [
        "Operation & Maintenance (O&M) of Radiology Systems",
        "Equipment & Consumables for Radiology Equipment",
        "MGPS System for Radiology Equipment",
        "Compliance with Statutory, Medical, Health Safety Standards"
      ]
    },
    {
      category: "Software & Integration",
      icon: <Activity size={32} style={{ color: 'var(--brand-red)', filter: 'brightness(1.2)' }} />,
      items: [
        "Software Compatible with Cath Lab Equipment",
        "Software Compatible with CT Equipment",
        "Software Compatible with MRI Equipment",
        "Software Compatible with DR Equipment"
      ]
    }
  ];

  const pathologyProducts = [
    {
      category: "Pathology Lab Infrastructure",
      icon: <TestTube size={32} style={{ color: 'var(--brand-brown)' }} />,
      items: [
        "Design & Engineering of Pathology Labs",
        "Statutory Approvals for Pathology Facilities",
        "Procurement of Laboratory Equipment",
        "Construction of Pathology Lab Infrastructure",
        "Supply & Installation of Lab Equipment",
        "Testing & Validation of Systems",
        "Commissioning of Complete Lab Setup"
      ]
    },
    {
      category: "Operations & Management",
      icon: <Activity size={32} style={{ color: 'var(--brand-brown)' }} />,
      items: [
        "Operation & Maintenance (O&M)",
        "Hospital Management Services",
        "Complete Medical Staffing",
        "Paramedical Staff Solutions",
        "Technical Support Staff",
        "Administrative Staff Management",
        "Facility Management Staff"
      ]
    }
  ];

  const surgeryProducts = [
    {
      category: "Modular Surgery & Critical Care Units",
      icon: <Stethoscope size={32} style={{ color: '#2563eb' }} />,
      items: [
        "Modular MOT (Modular Operation Theatre)",
        "Modular MICU (Medical ICU)",
        "Modular SICU (Surgical ICU)",
        "Modular Cardiac ICU",
        "Modular Neuro ICU",
        "Modular PICU (Pediatric ICU)",
        "Modular NICU (Neonatal ICU)",
        "Modular Trauma ICU",
        "Modular Burns ICU",
        "Modular Transplant ICU"
      ]
    },
    {
      category: "Medical Equipment & Instruments",
      icon: <Heart size={32} style={{ color: '#2563eb' }} />,
      items: [
        "Ventilators",
        "Patient Monitors",
        "Defibrillators",
        "Syringe Pump and Infusion Pump",
        "Laparoscopy Equipment",
        "Arthroscopy Equipment",
        "Surgical Instrument Sets"
      ]
    },
    {
      category: "EPC & Hospital Management",
      icon: <Building2 size={32} style={{ color: '#2563eb' }} />,
      items: [
        "Planning, Design & Execution of EPC Projects",
        "Design & Engineering",
        "Statutory Approvals",
        "Procurement & Construction",
        "Supply & Installation",
        "Testing, Validation & Commissioning",
        "Operation & Maintenance (O&M)",
        "Hospital Management with Complete Staffing"
      ]
    }
  ];

  const gynecologyProducts = [
    {
      category: "Maternal Health Infrastructure",
      icon: <Baby size={32} style={{ color: '#ec4899' }} />,
      items: [
        "Concepts & Designs of Maternal Health Infrastructure",
        "Strategic Planning for Women's Health Facilities",
        "Designing Maternal Care Units",
        "Execution of Gynecology Departments"
      ]
    },
    {
      category: "Management & Staffing",
      icon: <Activity size={32} style={{ color: '#ec4899' }} />,
      items: [
        "Complete Administrative Management",
        "Facility Staffing Solutions",
        "Specialized Gynecology Staff",
        "Support Staff Management"
      ]
    },
    {
      category: "Screening & Monitoring",
      icon: <Heart size={32} style={{ color: '#ec4899' }} />,
      items: [
        "Non Communicable Disease Screening",
        "Health Tracking Systems",
        "Patient Monitoring Solutions",
        "Preventive Care Programs"
      ]
    }
  ];

  const oncologyProducts = [
    {
      category: "Oncology Department Infrastructure",
      icon: <Zap size={32} style={{ color: '#9333ea' }} />,
      items: [
        "Design & Engineering of Oncology Departments",
        "Statutory Approvals for Cancer Care Facilities",
        "Procurement of Oncology Equipment",
        "Construction of Cancer Treatment Centers",
        "Supply & Installation of Radiation Therapy Systems",
        "Testing & Validation of Oncology Systems",
        "Commissioning of Complete Oncology Departments"
      ]
    },
    {
      category: "Operations & Management",
      icon: <Activity size={32} style={{ color: '#9333ea' }} />,
      items: [
        "Operation & Maintenance (O&M)",
        "Hospital Management Services",
        "Complete Medical Staffing (Oncologists, Radiologists)",
        "Paramedical Staff Solutions",
        "Technical Support Staff",
        "Administrative Staff Management",
        "Facility Management Staff"
      ]
    }
  ];

  const cardiologyProducts = [
    {
      category: "Cardiology & Critical Care Infrastructure",
      icon: <HeartPulse size={32} style={{ color: '#06b6d4' }} />,
      items: [
        "Design & Engineering of Cardiology Departments",
        "Statutory Approvals for Cardiac Care Facilities",
        "Procurement of Cardiology Equipment",
        "Construction of Critical Care Units",
        "Supply & Installation of Cardiac Monitoring Systems",
        "Testing & Validation of Cardiology Systems",
        "Commissioning of Complete Cardiology Departments"
      ]
    },
    {
      category: "Operations & Management",
      icon: <Activity size={32} style={{ color: '#06b6d4' }} />,
      items: [
        "Operation & Maintenance (O&M)",
        "Hospital Management Services",
        "Complete Medical Staffing (Cardiologists, Intensivists)",
        "Paramedical Staff Solutions",
        "Technical Support Staff",
        "Administrative Staff Management",
        "Facility Management Staff"
      ]
    }
  ];

  const paediatricProducts = [
    {
      category: "Paediatric & Neonatology Infrastructure",
      icon: <Baby size={32} style={{ color: '#10b981' }} />,
      items: [
        "Design & Engineering of Paediatric Departments",
        "Statutory Approvals for Child Care Facilities",
        "Procurement of Neonatology Equipment",
        "Construction of NICU & Paediatric Units",
        "Supply & Installation of Paediatric Systems",
        "Testing & Validation of Neonatology Equipment",
        "Commissioning of Complete Paediatric Departments"
      ]
    },
    {
      category: "Operations & Management",
      icon: <Heart size={32} style={{ color: '#10b981' }} />,
      items: [
        "Operation & Maintenance (O&M)",
        "Hospital Management Services",
        "Complete Medical Staffing (Paediatricians, Neonatologists)",
        "Paramedical Staff Solutions",
        "Technical Support Staff",
        "Administrative Staff Management",
        "Facility Management Staff"
      ]
    }
  ];

  const nephrologyProducts = [
    {
      category: "Nephrology & Urology Infrastructure",
      icon: <Activity size={32} style={{ color: '#6366f1' }} />,
      items: [
        "Design & Engineering of Nephrology Departments",
        "Statutory Approvals for Renal Care Facilities",
        "Procurement of Dialysis & Urology Equipment",
        "Construction of Nephrology & Urology Units",
        "Supply & Installation of Renal Care Systems",
        "Testing & Validation of Dialysis Equipment",
        "Commissioning of Complete Nephrology Departments"
      ]
    },
    {
      category: "Operations & Management",
      icon: <Wrench size={32} style={{ color: '#6366f1' }} />,
      items: [
        "Operation & Maintenance (O&M)",
        "Hospital Management Services",
        "Complete Medical Staffing (Nephrologists, Urologists)",
        "Paramedical Staff Solutions",
        "Technical Support Staff",
        "Administrative Staff Management",
        "Facility Management Staff"
      ]
    }
  ];

  const orthopaedicProducts = [
    {
      category: "Orthopaedic, Neurology & Trauma Infrastructure",
      icon: <Bone size={32} style={{ color: '#f59e0b' }} />,
      items: [
        "Design & Engineering of Orthopaedic Departments",
        "Statutory Approvals for Trauma Care Facilities",
        "Procurement of Orthopaedic & Neurology Equipment",
        "Construction of Trauma Care Units",
        "Supply & Installation of Orthopaedic Systems",
        "Testing & Validation of Neurology Equipment",
        "Commissioning of Complete Orthopaedic Departments"
      ]
    },
    {
      category: "Operations & Management",
      icon: <Building2 size={32} style={{ color: '#f59e0b' }} />,
      items: [
        "Operation & Maintenance (O&M)",
        "Hospital Management Services",
        "Complete Medical Staffing (Orthopaedic Surgeons, Neurologists)",
        "Paramedical Staff Solutions",
        "Technical Support Staff",
        "Administrative Staff Management",
        "Facility Management Staff"
      ]
    }
  ];

  const ophthalmologyProducts = [
    {
      category: "Modular ENT & Ophthalmology Facilities",
      icon: <Eye size={32} style={{ color: '#8b5cf6' }} />,
      items: [
        "Modular ENT and Ophthal OPD",
        "Modular Audiometry Room",
        "Modular Ophthalmic Operation Theatre",
        "Modular ENT Operation Theatre",
        "Modular Endoscopy Suite"
      ]
    },
    {
      category: "ENT Equipment Supply",
      icon: <Radio size={32} style={{ color: '#8b5cf6' }} />,
      items: [
        "Operating Microscope",
        "Endoscopy Camera",
        "Radio Frequency Unit",
        "Plasma System",
        "Micro Drill and Micro Debrider",
        "Stroboscope Systems",
        "Co2 Lasers",
        "Navigation System",
        "Bronchoscopy System",
        "BERA and OAE Machine",
        "OPD & Operative Instruments",
        "Autoclave Machine"
      ]
    },
    {
      category: "Ophthalmology Equipment Supply",
      icon: <Heart size={32} style={{ color: '#8b5cf6' }} />,
      items: [
        "Lasik System",
        "Operating Microscope",
        "Phacoemulsification Machine",
        "Vitrectomy Machine",
        "A-Scan Machine",
        "OCT Machine",
        "Optical Biometer",
        "Auto Ref with Keratometry Machine",
        "Vision Chair Unit",
        "Operative Instruments",
        "Applanation Tonometers",
        "Digital Fundus Camera",
        "Ophthalmoscope",
        "Retinoscope",
        "Green Laser",
        "Yag Laser",
        "Specular Microscope",
        "Dry Eye Screening & Treatment System",
        "Flash Autoclave Machine"
      ]
    },
    {
      category: "Operations & Management",
      icon: <Building2 size={32} style={{ color: '#8b5cf6' }} />,
      items: [
        "Operation & Maintenance (O&M)",
        "Hospital Management Services",
        "Complete Medical Staffing (ENT Surgeons, Ophthalmologists)",
        "Paramedical Staff Solutions",
        "Technical Support Staff",
        "Administrative Staff Management",
        "Facility Management Staff"
      ]
    }
  ];

  const sportsRehabProducts = [
    {
      category: "Sports Science Health Clinic & Rehabilitation Centres",
      icon: <Dumbbell size={32} style={{ color: '#dc2626' }} />,
      items: [
        "Sports Science Health Clinic Setup",
        "Rehabilitation Centre Design & Construction",
        "Athletic Performance Assessment Labs",
        "Sports Injury Diagnosis Facilities",
        "Recovery & Wellness Centers"
      ]
    },
    {
      category: "Fitness Management and Tracking Clinical Set up",
      icon: <Activity size={32} style={{ color: '#dc2626' }} />,
      items: [
        "Clinical Fitness Assessment Units",
        "Performance Tracking Systems",
        "Athlete Monitoring Infrastructure",
        "Biomechanics Analysis Labs",
        "Body Composition Analysis Facilities"
      ]
    },
    {
      category: "Modular Physiotherapy & Rehabilitation Clinical Set up",
      icon: <Heart size={32} style={{ color: '#dc2626' }} />,
      items: [
        "Modular Physiotherapy Units",
        "Hydrotherapy Pool Infrastructure",
        "Electrotherapy Equipment Rooms",
        "Exercise Therapy Studios",
        "Manual Therapy Suites"
      ]
    },
    {
      category: "Strategic Planning, Design & Execution",
      icon: <Building2 size={32} style={{ color: '#dc2626' }} />,
      items: [
        "Strategic Planning for Sports Medicine Facilities",
        "Designing Sports Rehabilitation Centers",
        "Execution of Physiotherapy Departments",
        "Statutory Approvals for Sports Medicine",
        "Equipment Procurement & Installation"
      ]
    },
    {
      category: "Management & Staffing",
      icon: <Wrench size={32} style={{ color: '#dc2626' }} />,
      items: [
        "Complete Administrative Management",
        "Facility Staffing Solutions",
        "Sports Physiotherapists & Specialists",
        "Athletic Trainers & Coaches",
        "Support Staff Management",
        "Operation & Maintenance (O&M)"
      ]
    }
  ];

  const ngoCsrProducts = [
    {
      category: "Modular Medical Infrastructure",
      icon: <HandHeart size={32} style={{ color: '#059669' }} />,
      items: [
        "Modular NICU (Neonatal Intensive Care Unit)",
        "Modular PICU (Paediatric Intensive Care Unit)",
        "Smart Primary Health Centre",
        "Dialysis Centre Setup",
        "Modular Cancer Wards",
        "Day Care Cancer Screening Centre",
        "Mobile Eye Screening Units"
      ]
    },
    {
      category: "Diagnostic & Laboratory Infrastructure",
      icon: <TestTube size={32} style={{ color: '#059669' }} />,
      items: [
        "Microbiology Lab Setup",
        "Pathology Lab Infrastructure",
        "Medical Equipment Supply",
        "Pathology Equipment Supply",
        "Veterinary Medicines & Supplies"
      ]
    },
    {
      category: "Village Health & Outreach Programs",
      icon: <Heart size={32} style={{ color: '#059669' }} />,
      items: [
        "Village Health Camps",
        "Mobile Health Screening Units",
        "Community Health Programs",
        "Rural Healthcare Outreach",
        "Preventive Care Initiatives"
      ]
    },
    {
      category: "Renewable Energy & Sustainability",
      icon: <Sun size={32} style={{ color: '#059669' }} />,
      items: [
        "Solar Panel System in PHC Health Centres",
        "Renewable Energy in Anganwadi",
        "Solar Power for Schools & Villages",
        "Solar Lamps Distribution",
        "Green Energy Infrastructure"
      ]
    },
    {
      category: "Education & Community Development",
      icon: <Building2 size={32} style={{ color: '#059669' }} />,
      items: [
        "Smart Anganwadi Setup",
        "Science Lab Infrastructure",
        "Library Lab Setup",
        "Smart Classrooms",
        "Educational Facility Development"
      ]
    },
    {
      category: "Water & Sanitation",
      icon: <Droplets size={32} style={{ color: '#059669' }} />,
      items: [
        "Village Potable Water Supply",
        "Water Purification Systems",
        "Clean Water Infrastructure",
        "Sanitation Facilities",
        "Water Quality Management"
      ]
    },
    {
      category: "Management & Staffing",
      icon: <Wrench size={32} style={{ color: '#059669' }} />,
      items: [
        "Complete Administrative Management",
        "Facility Staffing Solutions",
        "Project Coordination & Execution",
        "Community Liaison Services",
        "Operation & Maintenance (O&M)"
      ]
    }
  ];

  const ncdCellProducts = [
    {
      category: "Population Based Screening Mobile Van",
      icon: <Truck size={32} style={{ color: '#0891b2' }} />,
      items: [
        "Cervical Cancer Screening Mobile Van",
        "Breast Cancer Screening Mobile Van",
        "Oral Cancer Screening Mobile Van",
        "Multi-Disease Screening Mobile Units",
        "Mobile Diagnostic Equipment",
        "On-site Testing & Monitoring Systems"
      ]
    },
    {
      category: "Modular Clinic with Health & Wellness Centres",
      icon: <Shield size={32} style={{ color: '#0891b2' }} />,
      items: [
        "Modular NCD Screening Clinics",
        "Health & Wellness Centre Setup",
        "Preventive Care Facilities",
        "Early Detection & Diagnosis Units",
        "Patient Counseling Centers",
        "Follow-up Care Infrastructure"
      ]
    },
    {
      category: "Screening & Monitoring Programs",
      icon: <Activity size={32} style={{ color: '#0891b2' }} />,
      items: [
        "Population Health Screening Programs",
        "NCD Risk Assessment Systems",
        "Health Data Monitoring & Analytics",
        "Community Health Surveillance",
        "Disease Registry Management"
      ]
    },
    {
      category: "Management & Staffing",
      icon: <Wrench size={32} style={{ color: '#0891b2' }} />,
      items: [
        "Complete Administrative Management",
        "Facility Staffing Solutions",
        "Medical & Paramedical Staff",
        "Technical Support Team",
        "Community Health Workers",
        "Operation & Maintenance (O&M)"
      ]
    }
  ];

  const cleanRoomsProducts = [
    {
      category: "Modular Operation Theatres",
      icon: <Heart size={32} style={{ color: '#7c3aed' }} />,
      items: [
        "Modular Operation Theatre Design & Engineering",
        "Turnkey Modular OT Construction",
        "Laminar Air Flow Systems",
        "HEPA Filtration Systems",
        "Modular Wall & Ceiling Panels",
        "Anti-static Flooring Systems",
        "OT Pendant Systems & Equipment"
      ]
    },
    {
      category: "BSL-3 Laboratory Infrastructure",
      icon: <FlaskConical size={32} style={{ color: '#7c3aed' }} />,
      items: [
        "BSL-3 Lab Concept & Design",
        "Containment Systems & Barriers",
        "HEPA Filtered Exhaust Systems",
        "Negative Pressure Environments",
        "Autoclave & Decontamination Chambers",
        "Personnel Air Showers",
        "BSL-3 Compliance & Certification"
      ]
    },
    {
      category: "BSL-2 Laboratory Infrastructure",
      icon: <TestTube size={32} style={{ color: '#7c3aed' }} />,
      items: [
        "BSL-2 Lab Design & Engineering",
        "Biosafety Cabinet Installation",
        "Controlled Access Systems",
        "Laboratory Ventilation Systems",
        "Waste Decontamination Systems",
        "Emergency Safety Equipment",
        "BSL-2 Compliance & Validation"
      ]
    },
    {
      category: "NBS Laboratory & Clean Rooms",
      icon: <Wind size={32} style={{ color: '#7c3aed' }} />,
      items: [
        "NBS Laboratory Setup",
        "ISO Class Clean Rooms (Class 100 to 100,000)",
        "Pharmaceutical Clean Rooms",
        "Electronics Manufacturing Clean Rooms",
        "Air Handling Unit (AHU) Systems",
        "Pass Box & Air Lock Systems",
        "Environmental Monitoring Systems"
      ]
    },
    {
      category: "EPC Turnkey Services",
      icon: <Building2 size={32} style={{ color: '#7c3aed' }} />,
      items: [
        "Concept Development & Feasibility",
        "Design & Engineering",
        "Statutory Approvals & Compliance",
        "Procurement & Construction",
        "Supply & Installation",
        "Testing & Validation",
        "Commissioning & Handover"
      ]
    },
    {
      category: "Operations & Management",
      icon: <Wrench size={32} style={{ color: '#7c3aed' }} />,
      items: [
        "Operation & Maintenance (O&M)",
        "Hospital Management Services",
        "Complete Technical Staffing",
        "Administrative Staff Management",
        "Facility Management Staff",
        "Preventive Maintenance Programs",
        "24/7 Technical Support"
      ]
    }
  ];

  const pharmacyProducts = [
    {
      category: "Planning, Design & Execution",
      icon: <Store size={32} style={{ color: '#ea580c' }} />,
      items: [
        "Pharmacy Layout Planning & Design",
        "Medical Store Space Optimization",
        "Workflow Analysis & Design",
        "Regulatory Compliance Planning",
        "Custom Storage Solutions Design",
        "Cold Chain Infrastructure Planning"
      ]
    },
    {
      category: "EPC Project Services",
      icon: <Building2 size={32} style={{ color: '#ea580c' }} />,
      items: [
        "Engineering & Construction",
        "Statutory Approvals & Licensing",
        "Procurement of Equipment & Fixtures",
        "Civil & Interior Works",
        "MEP (Mechanical, Electrical, Plumbing)",
        "Fire Safety & Security Systems"
      ]
    },
    {
      category: "Supply & Installation",
      icon: <Pill size={32} style={{ color: '#ea580c' }} />,
      items: [
        "Pharmacy Shelving & Racking Systems",
        "Refrigeration & Cold Storage Units",
        "Dispensing Counters & Workstations",
        "Drug Storage Cabinets",
        "Inventory Management Systems",
        "Point of Sale (POS) Systems"
      ]
    },
    {
      category: "Testing, Validation & Commissioning",
      icon: <Activity size={32} style={{ color: '#ea580c' }} />,
      items: [
        "Equipment Testing & Calibration",
        "Temperature Mapping & Validation",
        "System Integration Testing",
        "Regulatory Compliance Validation",
        "Final Commissioning & Handover",
        "Staff Training & Documentation"
      ]
    },
    {
      category: "Customised Solutions",
      icon: <Wrench size={32} style={{ color: '#ea580c' }} />,
      items: [
        "Hospital Pharmacy Solutions",
        "Retail Pharmacy Setup",
        "Warehouse & Distribution Centers",
        "Specialty Pharmacy Infrastructure",
        "24-Hour Pharmacy Design",
        "Drive-Through Pharmacy Systems"
      ]
    },
    {
      category: "Operations & Management",
      icon: <Heart size={32} style={{ color: '#ea580c' }} />,
      items: [
        "Operation & Maintenance (O&M)",
        "Complete Administrative Management",
        "Pharmacist & Staff Placement",
        "Inventory Management Services",
        "Facility Management Staff",
        "Regulatory Compliance Support"
      ]
    }
  ];

  const reproductiveHealthProducts = [
    {
      category: "Maternal Health & Childbirth",
      icon: <Baby size={32} style={{ color: '#db2777' }} />,
      items: [
        "Antenatal Care (ANC) Facilities",
        "Labour & Delivery Rooms",
        "Postnatal Care Units",
        "High-Risk Pregnancy Management",
        "Maternal ICU Infrastructure",
        "Breastfeeding Support Centers",
        "Safe Motherhood Programs"
      ]
    },
    {
      category: "Family Planning & Contraception",
      icon: <Users size={32} style={{ color: '#db2777' }} />,
      items: [
        "Family Planning Clinics",
        "Contraception Counseling Centers",
        "IUD & Implant Services",
        "Sterilization Procedure Facilities",
        "Male Contraception Services",
        "Post-Partum Family Planning",
        "Community Outreach Programs"
      ]
    },
    {
      category: "Adolescent Health",
      icon: <Sparkles size={32} style={{ color: '#db2777' }} />,
      items: [
        "Adolescent Friendly Health Clinics",
        "Menstrual Health & Hygiene Programs",
        "Sexual & Reproductive Health Education",
        "Youth Counseling Centers",
        "Nutrition & Growth Monitoring",
        "Mental Health Support Services",
        "School Health Programs"
      ]
    },
    {
      category: "Reproductive Health Infrastructure",
      icon: <Building2 size={32} style={{ color: '#db2777' }} />,
      items: [
        "Design & Engineering of RH Facilities",
        "Statutory Approvals & Compliance",
        "Procurement of RH Equipment",
        "Construction of Women's Health Centers",
        "Supply & Installation of Medical Equipment",
        "Testing & Commissioning"
      ]
    },
    {
      category: "Operations & Management",
      icon: <Wrench size={32} style={{ color: '#db2777' }} />,
      items: [
        "Operation & Maintenance (O&M)",
        "Complete Administrative Management",
        "Gynecologists & Midwives Staffing",
        "Paramedical Staff Solutions",
        "Facility Management Staff",
        "Community Health Workers"
      ]
    }
  ];

  const epcServices = [
    "Design & Engineering",
    "Statutory Approvals",
    "Procurement",
    "Civil Construction",
    "MEP (Mechanical, Electrical, Plumbing)",
    "Medical Equipment Supply & Installation",
    "Testing & Validation",
    "Commissioning"
  ];

  const operationsServices = [
    "Operation & Maintenance (O&M)",
    "Hospital Management",
    "Medical Staffing",
    "Paramedical Staffing",
    "Technical Support Staff",
    "Administrative Staff",
    "Facility Management Staff"
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="section-padding" style={{ background: 'var(--bg-primary)' }}>
        <div className="container text-center">
          <h1 className="hero-large mb-6" style={{ lineHeight: '1.5' }}>
            <span style={{ color: 'var(--brand-orange)' }}>Infrastructure</span>
            {' • '}
            <span style={{ color: 'var(--brand-teal)' }}>Furniture</span>
            {' • '}
            <span style={{ color: 'var(--brand-red)' }}>Radiology</span>
            {' • '}
            <span style={{ color: 'var(--brand-brown)' }}>Pathology</span>
            {' • '}
            <span style={{ color: '#2563eb' }}>Surgery</span>
            {' • '}
            <span style={{ color: '#ec4899' }}>Gynecology</span>
            {' • '}
            <span style={{ color: '#9333ea' }}>Oncology</span>
            {' • '}
            <span style={{ color: '#06b6d4' }}>Cardiology</span>
            {' • '}
            <span style={{ color: '#10b981' }}>Paediatrics</span>
            {' • '}
            <span style={{ color: '#6366f1' }}>Nephrology</span>
            {' • '}
            <span style={{ color: '#f59e0b' }}>Orthopaedics</span>
            {' • '}
            <span style={{ color: '#8b5cf6' }}>Ophthalmology</span>
            {' • '}
            <span style={{ color: '#dc2626' }}>Sports Rehab</span>
            {' • '}
            <span style={{ color: '#059669' }}>NGO & CSR</span>
            {' • '}
            <span style={{ color: '#0891b2' }}>NCD Cell</span>
            {' • '}
            <span style={{ color: '#7c3aed' }}>Clean Rooms</span>
            {' • '}
            <span style={{ color: '#ea580c' }}>Pharmacy</span>
            {' • '}
            <span style={{ color: '#db2777' }}>Reproductive Health</span>
          </h1>
          <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '950px', margin: '0 auto' }}>
            Comprehensive EPC Turnkey Projects covering hospital infrastructure, specialized departments, modular clean rooms, BSL laboratories, pharmacy & medical stores, reproductive health, and community health programs.
          </p>
        </div>
      </section>

      {/* EPC Turnkey Overview */}
      <section className="section-padding" style={{ background: 'var(--bg-secondary)' }}>
        <div className="container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="hero-medium mb-6">
                <span style={{ color: 'var(--brand-teal)' }}>Specialty EPC</span>
                {' '}
                <span style={{ color: 'var(--brand-orange)' }}>Turnkey Projects</span>
              </h2>
              <p className="body-large mb-6" style={{ color: 'var(--text-secondary)' }}>
                Comprehensive turnkey solutions covering every aspect of healthcare infrastructure development. From initial design to final commissioning, we deliver world-class medical facilities.
              </p>
              <div className="grid grid-cols-2 gap-4">
                {epcServices.map((service, index) => (
                  <div key={index} className="flex items-start gap-2">
                    <CheckCircle2 size={20} style={{ color: 'var(--brand-teal)', flexShrink: 0, marginTop: '2px' }} />
                    <span className="body-regular" style={{ color: 'var(--text-primary)' }}>
                      {service}
                    </span>
                  </div>
                ))}
              </div>
            </div>
            <div 
              style={{
                background: 'var(--bg-primary)',
                padding: '48px',
                borderRadius: '8px',
                border: '2px solid var(--brand-orange)'
              }}
            >
              <h3 className="heading-2 mb-4" style={{ color: 'var(--brand-teal)' }}>
                Complete Project Lifecycle
              </h3>
              <p className="body-regular mb-4" style={{ color: 'var(--text-secondary)' }}>
                Our EPC model ensures seamless integration of:
              </p>
              <ul className="space-y-3">
                <li className="body-regular" style={{ color: 'var(--text-primary)' }}>
                  • <strong>Civil Works:</strong> Complete structural construction
                </li>
                <li className="body-regular" style={{ color: 'var(--text-primary)' }}>
                  • <strong>MEP Systems:</strong> Mechanical, electrical, plumbing integration
                </li>
                <li className="body-regular" style={{ color: 'var(--text-primary)' }}>
                  • <strong>Medical Equipment:</strong> State-of-the-art technology installation
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Infrastructure Products Grid */}
      <section className="section-padding" style={{ background: 'var(--bg-primary)' }}>
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="hero-medium mb-4">
              <span style={{ color: 'var(--brand-orange)' }}>Hospital Infrastructure</span>
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '800px', margin: '0 auto' }}>
              Comprehensive range of modular facilities designed, engineered, and delivered to international standards
            </p>
          </div>

          <div className="grid grid-cols-1 gap-12">
            {infrastructureProducts.map((product, index) => (
              <div 
                key={index}
                style={{
                  background: 'var(--bg-secondary)',
                  padding: '48px',
                  borderRadius: '8px',
                  border: '1px solid var(--border-light)'
                }}
              >
                <div className="flex items-start gap-4 mb-6">
                  <div 
                    style={{
                      background: 'var(--bg-primary)',
                      padding: '16px',
                      borderRadius: '8px',
                      border: '2px solid var(--brand-orange)'
                    }}
                  >
                    {product.icon}
                  </div>
                  <div>
                    <h3 className="heading-1 mb-2" style={{ color: 'var(--brand-teal)' }}>
                      {product.category}
                    </h3>
                    <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                      Complete turnkey solutions with full compliance
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {product.items.map((item, itemIndex) => (
                    <div 
                      key={itemIndex}
                      style={{
                        background: 'var(--bg-primary)',
                        padding: '16px 20px',
                        borderRadius: '4px',
                        border: '1px solid var(--border-light)',
                        transition: 'all 0.3s ease'
                      }}
                      className="product-item-card"
                    >
                      <div className="flex items-start gap-3">
                        <CheckCircle2 
                          size={18} 
                          style={{ 
                            color: 'var(--brand-teal)', 
                            flexShrink: 0,
                            marginTop: '2px'
                          }} 
                        />
                        <span className="body-regular" style={{ color: 'var(--text-primary)' }}>
                          {item}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Medical Furniture Products Grid */}
      <section className="section-padding" style={{ background: 'var(--bg-secondary)' }}>
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="hero-medium mb-4">
              <span style={{ color: 'var(--brand-teal)' }}>Hospital Medical Furniture</span>
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '800px', margin: '0 auto' }}>
              Complete range of medical furniture solutions for supply and installation across all healthcare environments
            </p>
          </div>

          <div className="grid grid-cols-1 gap-12">
            {medicalFurnitureProducts.map((product, index) => (
              <div 
                key={index}
                style={{
                  background: 'var(--bg-primary)',
                  padding: '48px',
                  borderRadius: '8px',
                  border: '1px solid var(--border-light)'
                }}
              >
                <div className="flex items-start gap-4 mb-6">
                  <div 
                    style={{
                      background: 'var(--bg-secondary)',
                      padding: '16px',
                      borderRadius: '8px',
                      border: '2px solid var(--brand-teal)'
                    }}
                  >
                    {product.icon}
                  </div>
                  <div>
                    <h3 className="heading-1 mb-2" style={{ color: 'var(--brand-teal)' }}>
                      {product.category}
                    </h3>
                    <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                      Supply and installation of premium medical furniture
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {product.items.map((item, itemIndex) => (
                    <div 
                      key={itemIndex}
                      style={{
                        background: 'var(--bg-secondary)',
                        padding: '16px 20px',
                        borderRadius: '4px',
                        border: '1px solid var(--border-light)',
                        transition: 'all 0.3s ease'
                      }}
                      className="furniture-item-card"
                    >
                      <div className="flex items-start gap-3">
                        <CheckCircle2 
                          size={18} 
                          style={{ 
                            color: 'var(--brand-orange)', 
                            flexShrink: 0,
                            marginTop: '2px'
                          }} 
                        />
                        <span className="body-regular" style={{ color: 'var(--text-primary)' }}>
                          {item}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Radiology Products Grid */}
      <section className="section-padding" style={{ background: 'var(--bg-primary)' }}>
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="hero-medium mb-4">
              <span style={{ color: 'var(--brand-red)' }}>Radiology Solutions</span>
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '800px', margin: '0 auto' }}>
              Planning, Design, & Execution of EPC Projects for advanced radiology systems with complete compliance
            </p>
          </div>

          <div className="grid grid-cols-1 gap-12">
            {radiologyProducts.map((product, index) => (
              <div 
                key={index}
                style={{
                  background: 'var(--bg-secondary)',
                  padding: '48px',
                  borderRadius: '8px',
                  border: '1px solid var(--border-light)'
                }}
              >
                <div className="flex items-start gap-4 mb-6">
                  <div 
                    style={{
                      background: 'var(--bg-primary)',
                      padding: '16px',
                      borderRadius: '8px',
                      border: '2px solid var(--brand-red)'
                    }}
                  >
                    {product.icon}
                  </div>
                  <div>
                    <h3 className="heading-1 mb-2" style={{ color: 'var(--brand-red)' }}>
                      {product.category}
                    </h3>
                    <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                      Supply, Installation, Testing, Validation, and Commissioning
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {product.items.map((item, itemIndex) => (
                    <div 
                      key={itemIndex}
                      style={{
                        background: 'var(--bg-primary)',
                        padding: '16px 20px',
                        borderRadius: '4px',
                        border: '1px solid var(--border-light)',
                        transition: 'all 0.3s ease'
                      }}
                      className="radiology-item-card"
                    >
                      <div className="flex items-start gap-3">
                        <CheckCircle2 
                          size={18} 
                          style={{ 
                            color: 'var(--brand-red)', 
                            flexShrink: 0,
                            marginTop: '2px'
                          }} 
                        />
                        <span className="body-regular" style={{ color: 'var(--text-primary)' }}>
                          {item}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pathology Products Grid */}
      <section className="section-padding" style={{ background: 'var(--bg-secondary)' }}>
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="hero-medium mb-4">
              <span style={{ color: 'var(--brand-brown)' }}>Pathology Solutions</span>
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '800px', margin: '0 auto' }}>
              Design, Engineering, Statutory Approvals, and Complete Turnkey Pathology Lab Solutions
            </p>
          </div>

          <div className="grid grid-cols-1 gap-12">
            {pathologyProducts.map((product, index) => (
              <div 
                key={index}
                style={{
                  background: 'var(--bg-primary)',
                  padding: '48px',
                  borderRadius: '8px',
                  border: '1px solid var(--border-light)'
                }}
              >
                <div className="flex items-start gap-4 mb-6">
                  <div 
                    style={{
                      background: 'var(--bg-secondary)',
                      padding: '16px',
                      borderRadius: '8px',
                      border: '2px solid var(--brand-brown)'
                    }}
                  >
                    {product.icon}
                  </div>
                  <div>
                    <h3 className="heading-1 mb-2" style={{ color: 'var(--brand-brown)' }}>
                      {product.category}
                    </h3>
                    <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                      Complete turnkey solutions with full compliance and staffing
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {product.items.map((item, itemIndex) => (
                    <div 
                      key={itemIndex}
                      style={{
                        background: 'var(--bg-secondary)',
                        padding: '16px 20px',
                        borderRadius: '4px',
                        border: '1px solid var(--border-light)',
                        transition: 'all 0.3s ease'
                      }}
                      className="pathology-item-card"
                    >
                      <div className="flex items-start gap-3">
                        <CheckCircle2 
                          size={18} 
                          style={{ 
                            color: 'var(--brand-brown)', 
                            flexShrink: 0,
                            marginTop: '2px'
                          }} 
                        />
                        <span className="body-regular" style={{ color: 'var(--text-primary)' }}>
                          {item}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Surgery & Critical Care Products Grid */}
      <section className="section-padding" style={{ background: 'var(--bg-primary)' }}>
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="hero-medium mb-4">
              <span style={{ color: '#2563eb' }}>Surgery & Critical Care and Anaesthesiology</span>
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '800px', margin: '0 auto' }}>
              Planning, Design & Execution of EPC Projects for Modular Surgery Units, Critical Care, and Anaesthesiology Equipment
            </p>
          </div>

          <div className="grid grid-cols-1 gap-12">
            {surgeryProducts.map((product, index) => (
              <div 
                key={index}
                style={{
                  background: 'var(--bg-secondary)',
                  padding: '48px',
                  borderRadius: '8px',
                  border: '1px solid var(--border-light)'
                }}
              >
                <div className="flex items-start gap-4 mb-6">
                  <div 
                    style={{
                      background: 'var(--bg-primary)',
                      padding: '16px',
                      borderRadius: '8px',
                      border: '2px solid #2563eb'
                    }}
                  >
                    {product.icon}
                  </div>
                  <div>
                    <h3 className="heading-1 mb-2" style={{ color: '#2563eb' }}>
                      {product.category}
                    </h3>
                    <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                      Supply, Installation, Testing, Validation, and Commissioning
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {product.items.map((item, itemIndex) => (
                    <div 
                      key={itemIndex}
                      style={{
                        background: 'var(--bg-primary)',
                        padding: '16px 20px',
                        borderRadius: '4px',
                        border: '1px solid var(--border-light)',
                        transition: 'all 0.3s ease'
                      }}
                      className="surgery-item-card"
                    >
                      <div className="flex items-start gap-3">
                        <CheckCircle2 
                          size={18} 
                          style={{ 
                            color: '#2563eb', 
                            flexShrink: 0,
                            marginTop: '2px'
                          }} 
                        />
                        <span className="body-regular" style={{ color: 'var(--text-primary)' }}>
                          {item}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gynecology Products Grid */}
      <section className="section-padding" style={{ background: 'var(--bg-secondary)' }}>
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="hero-medium mb-4">
              <span style={{ color: '#ec4899' }}>Gynecology & Maternal Health</span>
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '800px', margin: '0 auto' }}>
              Strategic Planning, Design & Execution of Maternal Health Infrastructure with Comprehensive Management Solutions
            </p>
          </div>

          <div className="grid grid-cols-1 gap-12">
            {gynecologyProducts.map((product, index) => (
              <div 
                key={index}
                style={{
                  background: 'var(--bg-primary)',
                  padding: '48px',
                  borderRadius: '8px',
                  border: '1px solid var(--border-light)'
                }}
              >
                <div className="flex items-start gap-4 mb-6">
                  <div 
                    style={{
                      background: 'var(--bg-secondary)',
                      padding: '16px',
                      borderRadius: '8px',
                      border: '2px solid #ec4899'
                    }}
                  >
                    {product.icon}
                  </div>
                  <div>
                    <h3 className="heading-1 mb-2" style={{ color: '#ec4899' }}>
                      {product.category}
                    </h3>
                    <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                      Complete strategic planning and execution solutions
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {product.items.map((item, itemIndex) => (
                    <div 
                      key={itemIndex}
                      style={{
                        background: 'var(--bg-secondary)',
                        padding: '16px 20px',
                        borderRadius: '4px',
                        border: '1px solid var(--border-light)',
                        transition: 'all 0.3s ease'
                      }}
                      className="gynecology-item-card"
                    >
                      <div className="flex items-start gap-3">
                        <CheckCircle2 
                          size={18} 
                          style={{ 
                            color: '#ec4899', 
                            flexShrink: 0,
                            marginTop: '2px'
                          }} 
                        />
                        <span className="body-regular" style={{ color: 'var(--text-primary)' }}>
                          {item}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Oncology Products Grid */}
      <section className="section-padding" style={{ background: 'var(--bg-primary)' }}>
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="hero-medium mb-4">
              <span style={{ color: '#9333ea' }}>Oncology & Cancer Care</span>
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '800px', margin: '0 auto' }}>
              Design, Engineering, Statutory Approvals, and Complete Turnkey Oncology Department Solutions
            </p>
          </div>

          <div className="grid grid-cols-1 gap-12">
            {oncologyProducts.map((product, index) => (
              <div 
                key={index}
                style={{
                  background: 'var(--bg-secondary)',
                  padding: '48px',
                  borderRadius: '8px',
                  border: '1px solid var(--border-light)'
                }}
              >
                <div className="flex items-start gap-4 mb-6">
                  <div 
                    style={{
                      background: 'var(--bg-primary)',
                      padding: '16px',
                      borderRadius: '8px',
                      border: '2px solid #9333ea'
                    }}
                  >
                    {product.icon}
                  </div>
                  <div>
                    <h3 className="heading-1 mb-2" style={{ color: '#9333ea' }}>
                      {product.category}
                    </h3>
                    <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                      Complete turnkey solutions with full compliance and staffing
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {product.items.map((item, itemIndex) => (
                    <div 
                      key={itemIndex}
                      style={{
                        background: 'var(--bg-primary)',
                        padding: '16px 20px',
                        borderRadius: '4px',
                        border: '1px solid var(--border-light)',
                        transition: 'all 0.3s ease'
                      }}
                      className="oncology-item-card"
                    >
                      <div className="flex items-start gap-3">
                        <CheckCircle2 
                          size={18} 
                          style={{ 
                            color: '#9333ea', 
                            flexShrink: 0,
                            marginTop: '2px'
                          }} 
                        />
                        <span className="body-regular" style={{ color: 'var(--text-primary)' }}>
                          {item}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Cardiology Products Grid */}
      <section className="section-padding" style={{ background: 'var(--bg-secondary)' }}>
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="hero-medium mb-4">
              <span style={{ color: '#06b6d4' }}>Cardiology & Critical Care</span>
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '800px', margin: '0 auto' }}>
              Design, Engineering, Statutory Approvals, and Complete Turnkey Cardiology and Critical Care Solutions
            </p>
          </div>

          <div className="grid grid-cols-1 gap-12">
            {cardiologyProducts.map((product, index) => (
              <div 
                key={index}
                style={{
                  background: 'var(--bg-primary)',
                  padding: '48px',
                  borderRadius: '8px',
                  border: '1px solid var(--border-light)'
                }}
              >
                <div className="flex items-start gap-4 mb-6">
                  <div 
                    style={{
                      background: 'var(--bg-secondary)',
                      padding: '16px',
                      borderRadius: '8px',
                      border: '2px solid #06b6d4'
                    }}
                  >
                    {product.icon}
                  </div>
                  <div>
                    <h3 className="heading-1 mb-2" style={{ color: '#06b6d4' }}>
                      {product.category}
                    </h3>
                    <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                      Complete turnkey solutions with full compliance and staffing
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {product.items.map((item, itemIndex) => (
                    <div 
                      key={itemIndex}
                      style={{
                        background: 'var(--bg-secondary)',
                        padding: '16px 20px',
                        borderRadius: '4px',
                        border: '1px solid var(--border-light)',
                        transition: 'all 0.3s ease'
                      }}
                      className="cardiology-item-card"
                    >
                      <div className="flex items-start gap-3">
                        <CheckCircle2 
                          size={18} 
                          style={{ 
                            color: '#06b6d4', 
                            flexShrink: 0,
                            marginTop: '2px'
                          }} 
                        />
                        <span className="body-regular" style={{ color: 'var(--text-primary)' }}>
                          {item}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Paediatric Products Grid */}
      <section className="section-padding" style={{ background: 'var(--bg-primary)' }}>
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="hero-medium mb-4">
              <span style={{ color: '#10b981' }}>Paediatric & Neonatology</span>
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '800px', margin: '0 auto' }}>
              Design, Engineering, Statutory Approvals, and Complete Turnkey Paediatric and Neonatology Department Solutions
            </p>
          </div>

          <div className="grid grid-cols-1 gap-12">
            {paediatricProducts.map((product, index) => (
              <div 
                key={index}
                style={{
                  background: 'var(--bg-secondary)',
                  padding: '48px',
                  borderRadius: '8px',
                  border: '1px solid var(--border-light)'
                }}
              >
                <div className="flex items-start gap-4 mb-6">
                  <div 
                    style={{
                      background: 'var(--bg-primary)',
                      padding: '16px',
                      borderRadius: '8px',
                      border: '2px solid #10b981'
                    }}
                  >
                    {product.icon}
                  </div>
                  <div>
                    <h3 className="heading-1 mb-2" style={{ color: '#10b981' }}>
                      {product.category}
                    </h3>
                    <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                      Complete turnkey solutions with full compliance and staffing
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {product.items.map((item, itemIndex) => (
                    <div 
                      key={itemIndex}
                      style={{
                        background: 'var(--bg-primary)',
                        padding: '16px 20px',
                        borderRadius: '4px',
                        border: '1px solid var(--border-light)',
                        transition: 'all 0.3s ease'
                      }}
                      className="paediatric-item-card"
                    >
                      <div className="flex items-start gap-3">
                        <CheckCircle2 
                          size={18} 
                          style={{ 
                            color: '#10b981', 
                            flexShrink: 0,
                            marginTop: '2px'
                          }} 
                        />
                        <span className="body-regular" style={{ color: 'var(--text-primary)' }}>
                          {item}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Nephrology Products Grid */}
      <section className="section-padding" style={{ background: 'var(--bg-secondary)' }}>
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="hero-medium mb-4">
              <span style={{ color: '#6366f1' }}>Nephrology & Urology</span>
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '800px', margin: '0 auto' }}>
              Design, Engineering, Statutory Approvals, and Complete Turnkey Nephrology and Urology Department Solutions
            </p>
          </div>

          <div className="grid grid-cols-1 gap-12">
            {nephrologyProducts.map((product, index) => (
              <div 
                key={index}
                style={{
                  background: 'var(--bg-primary)',
                  padding: '48px',
                  borderRadius: '8px',
                  border: '1px solid var(--border-light)'
                }}
              >
                <div className="flex items-start gap-4 mb-6">
                  <div 
                    style={{
                      background: 'var(--bg-secondary)',
                      padding: '16px',
                      borderRadius: '8px',
                      border: '2px solid #6366f1'
                    }}
                  >
                    {product.icon}
                  </div>
                  <div>
                    <h3 className="heading-1 mb-2" style={{ color: '#6366f1' }}>
                      {product.category}
                    </h3>
                    <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                      Complete turnkey solutions with full compliance and staffing
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {product.items.map((item, itemIndex) => (
                    <div 
                      key={itemIndex}
                      style={{
                        background: 'var(--bg-secondary)',
                        padding: '16px 20px',
                        borderRadius: '4px',
                        border: '1px solid var(--border-light)',
                        transition: 'all 0.3s ease'
                      }}
                      className="nephrology-item-card"
                    >
                      <div className="flex items-start gap-3">
                        <CheckCircle2 
                          size={18} 
                          style={{ 
                            color: '#6366f1', 
                            flexShrink: 0,
                            marginTop: '2px'
                          }} 
                        />
                        <span className="body-regular" style={{ color: 'var(--text-primary)' }}>
                          {item}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Orthopaedic Products Grid */}
      <section className="section-padding" style={{ background: 'var(--bg-primary)' }}>
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="hero-medium mb-4">
              <span style={{ color: '#f59e0b' }}>Orthopaedic, Neurology & Trauma Care</span>
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '800px', margin: '0 auto' }}>
              Design, Engineering, Statutory Approvals, and Complete Turnkey Orthopaedic, Neurology, and Trauma Care Solutions
            </p>
          </div>

          <div className="grid grid-cols-1 gap-12">
            {orthopaedicProducts.map((product, index) => (
              <div 
                key={index}
                style={{
                  background: 'var(--bg-secondary)',
                  padding: '48px',
                  borderRadius: '8px',
                  border: '1px solid var(--border-light)'
                }}
              >
                <div className="flex items-start gap-4 mb-6">
                  <div 
                    style={{
                      background: 'var(--bg-primary)',
                      padding: '16px',
                      borderRadius: '8px',
                      border: '2px solid #f59e0b'
                    }}
                  >
                    {product.icon}
                  </div>
                  <div>
                    <h3 className="heading-1 mb-2" style={{ color: '#f59e0b' }}>
                      {product.category}
                    </h3>
                    <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                      Complete turnkey solutions with full compliance and staffing
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {product.items.map((item, itemIndex) => (
                    <div 
                      key={itemIndex}
                      style={{
                        background: 'var(--bg-primary)',
                        padding: '16px 20px',
                        borderRadius: '4px',
                        border: '1px solid var(--border-light)',
                        transition: 'all 0.3s ease'
                      }}
                      className="orthopaedic-item-card"
                    >
                      <div className="flex items-start gap-3">
                        <CheckCircle2 
                          size={18} 
                          style={{ 
                            color: '#f59e0b', 
                            flexShrink: 0,
                            marginTop: '2px'
                          }} 
                        />
                        <span className="body-regular" style={{ color: 'var(--text-primary)' }}>
                          {item}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Ophthalmology & ENT Products Grid */}
      <section className="section-padding" style={{ background: 'var(--bg-secondary)' }}>
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="hero-medium mb-4">
              <span style={{ color: '#8b5cf6' }}>Ophthalmology & ENT</span>
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '800px', margin: '0 auto' }}>
              Complete Modular ENT and Ophthalmology Facilities with Comprehensive Equipment Supply and Management
            </p>
          </div>

          <div className="grid grid-cols-1 gap-12">
            {ophthalmologyProducts.map((product, index) => (
              <div 
                key={index}
                style={{
                  background: 'var(--bg-primary)',
                  padding: '48px',
                  borderRadius: '8px',
                  border: '1px solid var(--border-light)'
                }}
              >
                <div className="flex items-start gap-4 mb-6">
                  <div 
                    style={{
                      background: 'var(--bg-secondary)',
                      padding: '16px',
                      borderRadius: '8px',
                      border: '2px solid #8b5cf6'
                    }}
                  >
                    {product.icon}
                  </div>
                  <div>
                    <h3 className="heading-1 mb-2" style={{ color: '#8b5cf6' }}>
                      {product.category}
                    </h3>
                    <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                      Supply, Installation, Testing, Validation, and Commissioning
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {product.items.map((item, itemIndex) => (
                    <div 
                      key={itemIndex}
                      style={{
                        background: 'var(--bg-secondary)',
                        padding: '16px 20px',
                        borderRadius: '4px',
                        border: '1px solid var(--border-light)',
                        transition: 'all 0.3s ease'
                      }}
                      className="ophthalmology-item-card"
                    >
                      <div className="flex items-start gap-3">
                        <CheckCircle2 
                          size={18} 
                          style={{ 
                            color: '#8b5cf6', 
                            flexShrink: 0,
                            marginTop: '2px'
                          }} 
                        />
                        <span className="body-regular" style={{ color: 'var(--text-primary)' }}>
                          {item}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Sports Physiotherapy & Rehabilitation Products Grid */}
      <section className="section-padding" style={{ background: 'var(--bg-primary)' }}>
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="hero-medium mb-4">
              <span style={{ color: '#dc2626' }}>Sports Physiotherapy & Rehabilitation</span>
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '800px', margin: '0 auto' }}>
              Complete Sports Science Health Clinics, Rehabilitation Centres, Fitness Management, and Modular Physiotherapy Clinical Setups
            </p>
          </div>

          <div className="grid grid-cols-1 gap-12">
            {sportsRehabProducts.map((product, index) => (
              <div 
                key={index}
                style={{
                  background: 'var(--bg-secondary)',
                  padding: '48px',
                  borderRadius: '8px',
                  border: '1px solid var(--border-light)'
                }}
              >
                <div className="flex items-start gap-4 mb-6">
                  <div 
                    style={{
                      background: 'var(--bg-primary)',
                      padding: '16px',
                      borderRadius: '8px',
                      border: '2px solid #dc2626'
                    }}
                  >
                    {product.icon}
                  </div>
                  <div>
                    <h3 className="heading-1 mb-2" style={{ color: '#dc2626' }}>
                      {product.category}
                    </h3>
                    <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                      Complete strategic planning and execution solutions
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {product.items.map((item, itemIndex) => (
                    <div 
                      key={itemIndex}
                      style={{
                        background: 'var(--bg-primary)',
                        padding: '16px 20px',
                        borderRadius: '4px',
                        border: '1px solid var(--border-light)',
                        transition: 'all 0.3s ease'
                      }}
                      className="sports-rehab-item-card"
                    >
                      <div className="flex items-start gap-3">
                        <CheckCircle2 
                          size={18} 
                          style={{ 
                            color: '#dc2626', 
                            flexShrink: 0,
                            marginTop: '2px'
                          }} 
                        />
                        <span className="body-regular" style={{ color: 'var(--text-primary)' }}>
                          {item}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* NGO & CSR Project Activities Grid */}
      <section className="section-padding" style={{ background: 'var(--bg-secondary)' }}>
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="hero-medium mb-4">
              <span style={{ color: '#059669' }}>NGO & CSR Project Activities</span>
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '800px', margin: '0 auto' }}>
              Comprehensive NGO & CSR initiatives including modular medical infrastructure, renewable energy, education, and community development programs
            </p>
          </div>

          <div className="grid grid-cols-1 gap-12">
            {ngoCsrProducts.map((product, index) => (
              <div 
                key={index}
                style={{
                  background: 'var(--bg-primary)',
                  padding: '48px',
                  borderRadius: '8px',
                  border: '1px solid var(--border-light)'
                }}
              >
                <div className="flex items-start gap-4 mb-6">
                  <div 
                    style={{
                      background: 'var(--bg-secondary)',
                      padding: '16px',
                      borderRadius: '8px',
                      border: '2px solid #059669'
                    }}
                  >
                    {product.icon}
                  </div>
                  <div>
                    <h3 className="heading-1 mb-2" style={{ color: '#059669' }}>
                      {product.category}
                    </h3>
                    <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                      Complete strategic planning and execution solutions
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {product.items.map((item, itemIndex) => (
                    <div 
                      key={itemIndex}
                      style={{
                        background: 'var(--bg-secondary)',
                        padding: '16px 20px',
                        borderRadius: '4px',
                        border: '1px solid var(--border-light)',
                        transition: 'all 0.3s ease'
                      }}
                      className="ngo-csr-item-card"
                    >
                      <div className="flex items-start gap-3">
                        <CheckCircle2 
                          size={18} 
                          style={{ 
                            color: '#059669', 
                            flexShrink: 0,
                            marginTop: '2px'
                          }} 
                        />
                        <span className="body-regular" style={{ color: 'var(--text-primary)' }}>
                          {item}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Non-Communicable Diseases Cell Grid */}
      <section className="section-padding" style={{ background: 'var(--bg-primary)' }}>
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="hero-medium mb-4">
              <span style={{ color: '#0891b2' }}>Non-Communicable Diseases Cell</span>
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '800px', margin: '0 auto' }}>
              Population Based Screening and Monitoring Programs for Cervical, Breast & Oral Cancer with Modular Health & Wellness Centres
            </p>
          </div>

          <div className="grid grid-cols-1 gap-12">
            {ncdCellProducts.map((product, index) => (
              <div 
                key={index}
                style={{
                  background: 'var(--bg-secondary)',
                  padding: '48px',
                  borderRadius: '8px',
                  border: '1px solid var(--border-light)'
                }}
              >
                <div className="flex items-start gap-4 mb-6">
                  <div 
                    style={{
                      background: 'var(--bg-primary)',
                      padding: '16px',
                      borderRadius: '8px',
                      border: '2px solid #0891b2'
                    }}
                  >
                    {product.icon}
                  </div>
                  <div>
                    <h3 className="heading-1 mb-2" style={{ color: '#0891b2' }}>
                      {product.category}
                    </h3>
                    <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                      Complete screening, monitoring, and management solutions
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {product.items.map((item, itemIndex) => (
                    <div 
                      key={itemIndex}
                      style={{
                        background: 'var(--bg-primary)',
                        padding: '16px 20px',
                        borderRadius: '4px',
                        border: '1px solid var(--border-light)',
                        transition: 'all 0.3s ease'
                      }}
                      className="ncd-cell-item-card"
                    >
                      <div className="flex items-start gap-3">
                        <CheckCircle2 
                          size={18} 
                          style={{ 
                            color: '#0891b2', 
                            flexShrink: 0,
                            marginTop: '2px'
                          }} 
                        />
                        <span className="body-regular" style={{ color: 'var(--text-primary)' }}>
                          {item}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Modular Clean Rooms & Labs Grid */}
      <section className="section-padding" style={{ background: 'var(--bg-secondary)' }}>
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="hero-medium mb-4">
              <span style={{ color: '#7c3aed' }}>Modular Clean Rooms & Labs</span>
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '800px', margin: '0 auto' }}>
              Operation Theatres, BSL-3 Labs, BSL-2 Labs, NBS Labs & Clean Rooms - Complete EPC Turnkey Solutions from Concept to Commissioning
            </p>
          </div>

          <div className="grid grid-cols-1 gap-12">
            {cleanRoomsProducts.map((product, index) => (
              <div 
                key={index}
                style={{
                  background: 'var(--bg-primary)',
                  padding: '48px',
                  borderRadius: '8px',
                  border: '1px solid var(--border-light)'
                }}
              >
                <div className="flex items-start gap-4 mb-6">
                  <div 
                    style={{
                      background: 'var(--bg-secondary)',
                      padding: '16px',
                      borderRadius: '8px',
                      border: '2px solid #7c3aed'
                    }}
                  >
                    {product.icon}
                  </div>
                  <div>
                    <h3 className="heading-1 mb-2" style={{ color: '#7c3aed' }}>
                      {product.category}
                    </h3>
                    <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                      Complete design, engineering, and turnkey solutions
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {product.items.map((item, itemIndex) => (
                    <div 
                      key={itemIndex}
                      style={{
                        background: 'var(--bg-secondary)',
                        padding: '16px 20px',
                        borderRadius: '4px',
                        border: '1px solid var(--border-light)',
                        transition: 'all 0.3s ease'
                      }}
                      className="clean-rooms-item-card"
                    >
                      <div className="flex items-start gap-3">
                        <CheckCircle2 
                          size={18} 
                          style={{ 
                            color: '#7c3aed', 
                            flexShrink: 0,
                            marginTop: '2px'
                          }} 
                        />
                        <span className="body-regular" style={{ color: 'var(--text-primary)' }}>
                          {item}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Turnkey Pharmacy & Medical Stores Grid */}
      <section className="section-padding" style={{ background: 'var(--bg-primary)' }}>
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="hero-medium mb-4">
              <span style={{ color: '#ea580c' }}>Turnkey Pharmacy & Medical Stores</span>
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '800px', margin: '0 auto' }}>
              Planning, Design & Execution of EPC Projects - Supply, Installation, Testing, Validation & Commissioning of Customised Pharmacy Solutions
            </p>
          </div>

          <div className="grid grid-cols-1 gap-12">
            {pharmacyProducts.map((product, index) => (
              <div 
                key={index}
                style={{
                  background: 'var(--bg-secondary)',
                  padding: '48px',
                  borderRadius: '8px',
                  border: '1px solid var(--border-light)'
                }}
              >
                <div className="flex items-start gap-4 mb-6">
                  <div 
                    style={{
                      background: 'var(--bg-primary)',
                      padding: '16px',
                      borderRadius: '8px',
                      border: '2px solid #ea580c'
                    }}
                  >
                    {product.icon}
                  </div>
                  <div>
                    <h3 className="heading-1 mb-2" style={{ color: '#ea580c' }}>
                      {product.category}
                    </h3>
                    <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                      Complete customised pharmacy and medical store solutions
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {product.items.map((item, itemIndex) => (
                    <div 
                      key={itemIndex}
                      style={{
                        background: 'var(--bg-primary)',
                        padding: '16px 20px',
                        borderRadius: '4px',
                        border: '1px solid var(--border-light)',
                        transition: 'all 0.3s ease'
                      }}
                      className="pharmacy-item-card"
                    >
                      <div className="flex items-start gap-3">
                        <CheckCircle2 
                          size={18} 
                          style={{ 
                            color: '#ea580c', 
                            flexShrink: 0,
                            marginTop: '2px'
                          }} 
                        />
                        <span className="body-regular" style={{ color: 'var(--text-primary)' }}>
                          {item}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Reproductive Health Grid */}
      <section className="section-padding" style={{ background: 'var(--bg-secondary)' }}>
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="hero-medium mb-4">
              <span style={{ color: '#db2777' }}>Reproductive Health</span>
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '800px', margin: '0 auto' }}>
              Comprehensive Maternal Health, Family Planning & Adolescent Health Services - Complete Infrastructure & Management Solutions
            </p>
          </div>

          <div className="grid grid-cols-1 gap-12">
            {reproductiveHealthProducts.map((product, index) => (
              <div 
                key={index}
                style={{
                  background: 'var(--bg-primary)',
                  padding: '48px',
                  borderRadius: '8px',
                  border: '1px solid var(--border-light)'
                }}
              >
                <div className="flex items-start gap-4 mb-6">
                  <div 
                    style={{
                      background: 'var(--bg-secondary)',
                      padding: '16px',
                      borderRadius: '8px',
                      border: '2px solid #db2777'
                    }}
                  >
                    {product.icon}
                  </div>
                  <div>
                    <h3 className="heading-1 mb-2" style={{ color: '#db2777' }}>
                      {product.category}
                    </h3>
                    <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                      Complete reproductive health infrastructure and services
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {product.items.map((item, itemIndex) => (
                    <div 
                      key={itemIndex}
                      style={{
                        background: 'var(--bg-secondary)',
                        padding: '16px 20px',
                        borderRadius: '4px',
                        border: '1px solid var(--border-light)',
                        transition: 'all 0.3s ease'
                      }}
                      className="reproductive-health-item-card"
                    >
                      <div className="flex items-start gap-3">
                        <CheckCircle2 
                          size={18} 
                          style={{ 
                            color: '#db2777', 
                            flexShrink: 0,
                            marginTop: '2px'
                          }} 
                        />
                        <span className="body-regular" style={{ color: 'var(--text-primary)' }}>
                          {item}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Operations & Management */}
      <section className="section-padding" style={{ background: 'var(--bg-secondary)' }}>
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="hero-medium mb-4">
              <span style={{ color: 'var(--brand-teal)' }}>Operations & Management</span>
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '800px', margin: '0 auto' }}>
              Complete hospital operations with end-to-end staffing solutions for sustained excellence
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {operationsServices.map((service, index) => (
              <div 
                key={index}
                style={{
                  background: 'var(--bg-primary)',
                  padding: '32px',
                  borderRadius: '8px',
                  border: '1px solid var(--border-light)',
                  textAlign: 'center',
                  transition: 'all 0.3s ease'
                }}
                className="operations-card"
              >
                <CheckCircle2 
                  size={32} 
                  style={{ 
                    color: 'var(--brand-orange)',
                    margin: '0 auto 16px'
                  }} 
                />
                <p className="body-large" style={{ color: 'var(--text-primary)', fontWeight: '600' }}>
                  {service}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Key Features */}
      <section className="section-padding" style={{ background: 'var(--bg-secondary)' }}>
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="hero-medium mb-4">Why Choose Our Solutions?</h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '900px', margin: '0 auto' }}>
              Complete end-to-end healthcare solutions spanning infrastructure, furniture, diagnostics, laboratory, surgery, and critical care
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div 
              style={{
                background: 'var(--bg-primary)',
                padding: '40px',
                borderRadius: '8px',
                border: '2px solid var(--brand-orange)',
                textAlign: 'center'
              }}
            >
              <h3 className="heading-2 mb-4" style={{ color: 'var(--brand-orange)' }}>
                Turnkey Delivery
              </h3>
              <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                Single-point accountability from design to operations ensuring timely delivery.
              </p>
            </div>

            <div 
              style={{
                background: 'var(--bg-primary)',
                padding: '40px',
                borderRadius: '8px',
                border: '2px solid #2563eb',
                textAlign: 'center'
              }}
            >
              <h3 className="heading-2 mb-4" style={{ color: '#2563eb' }}>
                Surgery & Critical Care
              </h3>
              <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                Modular operation theatres and ICU units with complete anaesthesiology equipment.
              </p>
            </div>

            <div 
              style={{
                background: 'var(--bg-primary)',
                padding: '40px',
                borderRadius: '8px',
                border: '2px solid var(--brand-red)',
                textAlign: 'center'
              }}
            >
              <h3 className="heading-2 mb-4" style={{ color: 'var(--brand-red)' }}>
                Advanced Diagnostics
              </h3>
              <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                State-of-the-art radiology and pathology systems with complete EPC execution.
              </p>
            </div>

            <div 
              style={{
                background: 'var(--bg-primary)',
                padding: '40px',
                borderRadius: '8px',
                border: '2px solid var(--brand-brown)',
                textAlign: 'center'
              }}
            >
              <h3 className="heading-2 mb-4" style={{ color: 'var(--brand-brown)' }}>
                Complete Staffing
              </h3>
              <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                Medical, paramedical, technical, administrative, and facility management solutions.
              </p>
            </div>

            <div 
              style={{
                background: 'var(--bg-primary)',
                padding: '40px',
                borderRadius: '8px',
                border: '2px solid var(--brand-teal)',
                textAlign: 'center'
              }}
            >
              <h3 className="heading-2 mb-4" style={{ color: 'var(--brand-teal)' }}>
                Quality Equipment
              </h3>
              <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                Premium medical furniture, ventilators, monitors, and surgical instruments.
              </p>
            </div>

            <div 
              style={{
                background: 'var(--bg-primary)',
                padding: '40px',
                borderRadius: '8px',
                border: '2px solid var(--brand-orange)',
                textAlign: 'center'
              }}
            >
              <h3 className="heading-2 mb-4" style={{ color: 'var(--brand-orange)' }}>
                Full Compliance
              </h3>
              <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                Complete adherence to NMC, NABH, ICMR, HTM, NFPA, ISO healthcare standards.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding cta-section-dark">
        <div className="container text-center">
          <h2 className="hero-medium mb-4 cta-heading-light">
            Ready to Build World-Class Healthcare Infrastructure?
          </h2>
          <p className="body-large mb-8 cta-text-light">
            Discuss your hospital infrastructure requirements with our expert team.
          </p>
          <Link 
            to="/contact" 
            className="btn-primary btn-light-on-dark"
          >
            Request Project Consultation
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Products;
